import cx from 'classnames';
import React from 'react';

import { One } from '../../components/Decorations/One';
import { ButtonProps } from '../../components/buttons/Button';
import { ButtonGroup } from '../../components/buttons/ButtonGroup';
import { Link } from '../../components/buttons/Link';
import { IconLoader } from '../../components/images/IconLoader';
import { roundedOuterModuleClasses } from '../../components/module/Background';
import { SpaceType } from '../../components/module/SpacingOptions';
import { Text } from '../../components/module/Text';
import { Title } from '../../components/module/Title';
import { Wrapper } from '../../components/module/Wrapper';
import { joinList } from '../../helpers/utils/string';

export type LocationsProps = {
  theme?: {
    space?: SpaceType;
  };
  title?: string;
  intro?: React.ReactNode;
  buttons?: ButtonProps[];
  items?: {
    _key?: string;
    country?: string;
    offices?: string[];
    href?: string;
  }[];
};

const gridClasses: Record<1 | 2 | 3 | 4, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4',
};

export const Locations = ({
  theme,
  title,
  intro,
  items,
  buttons,
}: LocationsProps) => {
  return (
    <Wrapper theme={{ ...theme, background: 'brand-dark' }}>
      <div
        className={cx(
          'hidden md:block absolute inset-0 pointer-events-none overflow-hidden',
          roundedOuterModuleClasses.top,
          roundedOuterModuleClasses.bottom,
        )}
      >
        <One
          direction="up"
          color="brand-base"
          className="absolute left-0 translate-x-[-100%] top-0 xl:-translate-x-[85%]"
        />
        <One
          direction="down"
          color="brand-base"
          className="absolute right-0 top-0 translate-x-[110%] xl:translate-x-full -translate-y-[15%]"
        />
      </div>
      <div className="py-8 sm:py-10 lg:py-16 xl:py-30 flex flex-col gap-6 md:text-center md:items-center">
        {title && (
          <Title size="3xl" color="white">
            {title}
          </Title>
        )}

        {intro && (
          <Text color="white" align={null} className="opacity-75 md:text-center">
            {intro}
          </Text>
        )}

        {buttons && (
          <ButtonGroup
            className="mt-4"
            items={buttons.map((button) => ({
              ...button,
              variant: 'white',
            }))}
          />
        )}

        {Boolean(items?.length) && (
          <ul
            className={cx(
              'grid gap-4 md:gap-8 mt-4 sm:mt-6 md:mt-12 lg:mt-16 xl:mt-20 w-full md:justify-center',
              gridClasses[items.length],
            )}
          >
            {items?.map(({ country, offices, href, _key }) => (
              <li
                key={_key}
                className={cx(
                  'relative rounded-xl px-4 md:px-8 py-4 md:py-6 group',
                  'transition-all shadow-none hover:shadow-lg focus-within:shadow-lg',
                  'bg-white md:bg-transparent hover:bg-white focus-within:bg-white',
                  'border-2 border-white/40 hover:border-white focus-within:border-white',
                  'flex-1 flex flex-col gap-2',
                  'text-left',
                )}
              >
                {country && (
                  <Link href={href || '/'} className="outline-none">
                    <span className="absolute inset-0 bg-neutral-25 block opacity-0 z-10 cursor-pointer" />
                    <h3
                      className={cx(
                        'md:!leading-none',
                        'text-neutral-base md:text-white group-hover:text-neutral-base group-focus-within:text-neutral-base',
                        'font-bold text-title-md-sm md:text-title-md-sm lg:text-title-lg-lg',
                        'group-hover:underline group-focus-within:underline underline-offset-4 md:group-hover:no-underline md:group-focus-within:no-underline',
                        'w-full max-w-[calc(100%-50px)] break-words hyphens-auto',
                      )}
                      dangerouslySetInnerHTML={{
                        __html: country.replace('Inter', 'Inter&shy;'),
                      }}
                    />
                  </Link>
                )}

                {Boolean(offices?.length) && (
                  <span className="pr-12 text-sm md:text-title-sm-md lg:text-title-sm-lg text-brand-base md:text-white/75 group-hover:text-brand-base group-focus-within:text-brand-base font-bold">
                    {joinList(offices)}
                  </span>
                )}

                <div
                  className={cx(
                    'md:w-10 md:h-10 rounded-full flex items-center justify-center absolute right-4 md:right-8 top-1/2 -translate-y-1/2',
                    'text-action-base md:border-white md:text-white md:border-2 group-hover:border-action-base group-focus-within:border-action-base group-hover:text-action-base group-focus-within:text-action-base',
                  )}
                >
                  <IconLoader icon="chevron" className="w-5 h-5 -rotate-90" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Wrapper>
  );
};

export default React.memo(Locations);
